import { Icon, ICONS } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Accordion } from '@websrc/App/Components/Accordion/Accordion'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapSpot } from '@websrc/DataMappers/mapSpot'
import { Spot } from '@websrc/Spot/Spot'
import clsx from 'clsx'
import { styles, typography, visuallyHidden } from 'leds'
import React, { useContext, useMemo, useRef, useState } from 'react'

import { spacer } from './SpotCollapse.css'
import * as SC from './SpotCollapse.style'
import { SpotCollapseBar } from './SpotCollapseBar'

export interface SpotCollapseProps {
  spots: ReturnType<typeof mapSpot>[]
  spotIndex: number
  withSpacing?: boolean
}

export type LearnMoreButtonType = React.FC<{ position?: Array<string | undefined | null> }>

// eslint-disable-next-line complexity
export function SpotCollapse({ spots, spotIndex, withSpacing = true }: SpotCollapseProps) {
  const currentSpot = spots[spotIndex]
  const { spacing, accordionSettings } = currentSpot.globalSettings

  const [showCollapsedSpot, setShowCollapsedSpot] = useState(false)
  const [expandAccordion, setExpandAccordion] = useState(accordionSettings?.expanded ?? false)

  const t = useStaticText()
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const scrollLearnMoreRef = useRef<HTMLDivElement | null>(null)
  const scrollCollapseRef = useRef<HTMLDivElement | null>(null)
  const { matomoPageTemplate } = useContext(AppProviderContext)

  const collapsedSiblingSpots = useMemo(() => {
    const collapsedSiblings = []
    let siblingIndex = 1

    while (spots && spots[spotIndex + siblingIndex]?.globalSettings.collapsed) {
      collapsedSiblings.push(spots[spotIndex + siblingIndex])
      siblingIndex++
    }

    return collapsedSiblings
  }, [spots, spotIndex])

  const isFirstAccordionInGroup = useMemo(() => {
    if (!accordionSettings) {
      return false
    }

    // Find the first previous sibling that is not colllapsed and check if it is an accordion
    for (let siblingIndex = spotIndex - 1; siblingIndex >= 0; siblingIndex--) {
      const siblingSpot = spots[siblingIndex]

      if (!siblingSpot) {
        return true
      } else if (siblingSpot.globalSettings.collapsed) {
        continue
      }

      return siblingSpot.globalSettings.accordionSettings ? false : true
    }

    return true
  }, [spots, spotIndex, accordionSettings])

  if (currentSpot?.globalSettings.collapsed) {
    return null
  }

  const hasChildren = Boolean(collapsedSiblingSpots.length)

  const LearnMoreButton: LearnMoreButtonType | undefined = hasChildren
    ? ({ position }) => (
        <SC.Button
          onClick={() => {
            if (!showCollapsedSpot && scrollLearnMoreRef.current) {
              window.scrollTo({
                behavior: 'smooth',
                top: scrollLearnMoreRef.current.getBoundingClientRect().top + window.scrollY,
              })
            }

            setShowCollapsedSpot((s) => !s)

            if (position?.length) {
              matomo.trackEvent(
                'engagement',
                matomoPageTemplate,
                position,
                [t.spot.expand_spot],
                'button'
              )
            }
          }}
          ref={buttonRef}
        >
          <Icon icon={showCollapsedSpot ? ICONS.MINUS : ICONS.PLUS} />
          <span>{showCollapsedSpot ? t.spot.collapse_spot : t.spot.expand_spot}</span>
        </SC.Button>
      )
    : undefined

  const SpotContent = (
    <>
      {hasChildren && <div ref={scrollCollapseRef} />}
      <Spot
        forceNoSpacing={!withSpacing}
        imageLazy={!(spotIndex !== undefined && spotIndex === 0)}
        LearnMoreButton={LearnMoreButton}
        spot={currentSpot}
        spotIndex={spotIndex}
      />
      {hasChildren && <div ref={scrollLearnMoreRef} />}
      <div
        aria-hidden={!showCollapsedSpot}
        className={clsx(showCollapsedSpot ? null : visuallyHidden)}
      >
        {collapsedSiblingSpots.map((spot, i) => (
          <Spot key={`spot-child-${spotIndex}-${i}`} spot={spot} spotIndex={spotIndex} />
        ))}
      </div>
      {hasChildren && showCollapsedSpot && (
        <SpotCollapseBar
          buttonRef={buttonRef}
          onCollapse={() => setShowCollapsedSpot(false)}
          scrollRef={scrollCollapseRef}
        />
      )}
    </>
  )

  const Spacer = <div className={spacer[spacing]} />

  return accordionSettings ? (
    <>
      {isFirstAccordionInGroup && withSpacing && Spacer}
      <Accordion
        accordionId={accordionSettings.label}
        buttonClassName={styles({ paddingX: 20 })}
        className={styles({
          backgroundColor: 'grey1',
          borderLeftWidth: 1,
          borderRightWidth: 1,
          borderTopWidth: isFirstAccordionInGroup ? 1 : 0,
          borderStyle: 'solid',
          borderBottomWidth: 1,
          borderColor: 'grey4',
          borderBottomStyle: 'solid',
          overflowY: 'hidden',
        })}
        isOpen={expandAccordion}
        label={
          <span
            className={clsx(
              styles({ paddingTop: 25, paddingBottom: 25 }),
              typography({ size: 'xl3', weight: 'bold', align: 'left' })
            )}
          >
            {accordionSettings.label}
          </span>
        }
        onAccordionClick={(isOpen) => setExpandAccordion(!isOpen)}
      >
        <div className={styles({ paddingTop: 20, paddingBottom: 20 })}>{SpotContent}</div>
      </Accordion>
    </>
  ) : (
    <>
      {withSpacing && Spacer}
      {SpotContent}
    </>
  )
}
